// WYSIWYG formatting toolbar for inline editing. It edits the *actual authored
// DOM* inside the same-origin iframe via document.execCommand, so hand-authored
// markup/styling is preserved (a schema editor would normalize it away).
//
// Font/size/color choices are deliberately constrained to the Cellery design
// system (Nunito/Montserrat/Rubik + the brand palette). Every control carries a
// rich data-tip explaining what it does. All controls are buttons with
// onMouseDown preventDefault so the iframe text selection survives the click.

import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { Icon, type IconName } from "../components/Icon";

interface Props {
  exec: (command: string, value?: string) => void;
  getDoc: () => Document | null;
}

interface IconAction {
  icon: IconName;
  title: string;
  tip: string;
  command: string;
}

const BLOCKS: { label: string; tag: string; tip: string }[] = [
  { label: "H1", tag: "H1", tip: "Heading 1 — largest section title" },
  { label: "H2", tag: "H2", tip: "Heading 2 — subsection title" },
  { label: "H3", tag: "H3", tip: "Heading 3 — minor heading" },
  { label: "¶", tag: "P", tip: "Body paragraph — normal text" },
];

const FORMAT: IconAction[] = [
  { icon: "bold", title: "Bold", tip: "Bold (⌘/Ctrl+B) — emphasize the selected text", command: "bold" },
  { icon: "italic", title: "Italic", tip: "Italic (⌘/Ctrl+I) — slant the selected text", command: "italic" },
  { icon: "underline", title: "Underline", tip: "Underline (⌘/Ctrl+U) the selected text", command: "underline" },
  { icon: "list-ul", title: "Bulleted list", tip: "Bulleted list — turn lines into bullet points", command: "insertUnorderedList" },
  { icon: "list-ol", title: "Numbered list", tip: "Numbered list — turn lines into a numbered list", command: "insertOrderedList" },
  { icon: "clear-format", title: "Clear formatting", tip: "Clear formatting — strip bold/italic/colors from the selection", command: "removeFormat" },
];

const ALIGN_H: { icon: IconName; tip: string; command: string }[] = [
  { icon: "align-left", tip: "Align left", command: "justifyLeft" },
  { icon: "align-center", tip: "Align center (horizontal)", command: "justifyCenter" },
  { icon: "align-right", tip: "Align right", command: "justifyRight" },
  { icon: "align-justify", tip: "Justify — stretch lines to both edges", command: "justifyFull" },
];

const ALIGN_V: { icon: IconName; tip: string; pos: string }[] = [
  { icon: "align-top", tip: "Align top — push content to the top of the slide", pos: "flex-start" },
  { icon: "align-middle", tip: "Center vertically within the slide", pos: "center" },
  { icon: "align-bottom", tip: "Align bottom — push content to the bottom", pos: "flex-end" },
];

const FONTS: { label: string; note: string; css: string }[] = [
  { label: "Nunito", note: "Body & headings", css: "Nunito, system-ui, sans-serif" },
  { label: "Montserrat", note: "Display & numerics", css: "Montserrat, system-ui, sans-serif" },
  { label: "Rubik", note: "Captions", css: "Rubik, system-ui, sans-serif" },
];

const SIZES = ["14", "16", "18", "20", "24", "28", "32", "40", "48"];

const TEXT_COLORS: { name: string; hex: string }[] = [
  { name: "Navy", hex: "#202140" },
  { name: "Indigo", hex: "#2a2b52" },
  { name: "Bluish", hex: "#023e60" },
  { name: "Teal", hex: "#75bfb8" },
  { name: "Sage", hex: "#596c33" },
  { name: "Red", hex: "#b84239" },
  { name: "Muted", hex: "#5b5c72" },
  { name: "White", hex: "#ffffff" },
];
const HIGHLIGHTS: { name: string; hex: string }[] = [
  { name: "None", hex: "transparent" },
  { name: "Butter", hex: "#f6dfa4" },
  { name: "Tan", hex: "#f2efdf" },
  { name: "Teal", hex: "#cfeae7" },
  { name: "Sage", hex: "#e3e9d2" },
  { name: "Rose", hex: "#f0d7d4" },
];

/** A toolbar button that opens an anchored popover; closes on outside-click/Esc. */
function ToolDropdown({
  label,
  tip,
  children,
  width,
}: {
  label: ReactNode;
  tip: string;
  children: (close: () => void) => ReactNode;
  width?: number;
}) {
  const [open, setOpen] = useState(false);
  // Flip the popover to right-align when a left-anchored one would overflow the
  // viewport (e.g. a control that wrapped to the far right on a phone).
  const [alignRight, setAlignRight] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  useLayoutEffect(() => {
    if (!open || !ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const popW = Math.min(width ?? 200, window.innerWidth - 16);
    setAlignRight(rect.left + popW > window.innerWidth - 8);
  }, [open, width]);

  const popStyle: CSSProperties = {};
  if (width) popStyle.width = `min(${width}px, calc(100vw - 16px))`;
  if (alignRight) {
    popStyle.left = "auto";
    popStyle.right = 0;
  }

  return (
    <div className="tool-dropdown" ref={ref}>
      <button
        type="button"
        className="btn btn-ghost edit-tool edit-tool--text"
        data-tip={tip}
        aria-haspopup="true"
        aria-expanded={open}
        onPointerDown={(e) => e.preventDefault()}
        onClick={() => setOpen((o) => !o)}
      >
        {label}
        <Icon name="chevron-down" size={14} />
      </button>
      {open && (
        <div className="tool-pop" style={popStyle} role="menu">
          {children(() => setOpen(false))}
        </div>
      )}
    </div>
  );
}

/** Link control: an inline URL popover (replaces window.prompt so it works on
 *  touch). The iframe selection is saved before the input steals focus and
 *  restored right before execCommand runs. */
function LinkTool({
  exec,
  getDoc,
}: {
  exec: (command: string, value?: string) => void;
  getDoc: () => Document | null;
}) {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");
  const ref = useRef<HTMLDivElement | null>(null);
  const savedRange = useRef<Range | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const openPopover = () => {
    const sel = getDoc()?.getSelection();
    savedRange.current =
      sel && sel.rangeCount ? sel.getRangeAt(0).cloneRange() : null;
    const node = sel?.anchorNode ?? null;
    const el =
      node && node.nodeType === 1 ? (node as Element) : node?.parentElement ?? null;
    setUrl(el?.closest("a")?.getAttribute("href") ?? "");
    setOpen(true);
  };

  // Move focus back into the iframe and re-apply the saved selection so the
  // execCommand acts on the original text, not the (now-blurred) input.
  const restore = () => {
    const doc = getDoc();
    if (!doc || !savedRange.current) return;
    doc.defaultView?.focus();
    const sel = doc.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(savedRange.current);
  };

  const apply = () => {
    const v = url.trim();
    restore();
    if (v) exec("createLink", v);
    else exec("unlink");
    setOpen(false);
  };

  const remove = () => {
    restore();
    exec("unlink");
    setOpen(false);
  };

  return (
    <div className="tool-dropdown" ref={ref}>
      <button
        type="button"
        className="btn btn-ghost edit-tool icon-btn"
        data-tip="Link — wrap the selection in a hyperlink"
        aria-label="Link"
        aria-haspopup="true"
        aria-expanded={open}
        onPointerDown={(e) => e.preventDefault()}
        onClick={openPopover}
      >
        <Icon name="link" size={18} />
      </button>
      {open && (
        <div className="tool-pop tool-pop--link" role="menu">
          <form
            className="link-form"
            onSubmit={(e) => {
              e.preventDefault();
              apply();
            }}
          >
            <input
              className="input"
              type="url"
              placeholder="https://…"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              aria-label="Link URL"
              autoFocus
            />
            <div className="link-actions">
              <button type="button" className="btn btn-ghost" onClick={remove}>
                Remove
              </button>
              <button type="submit" className="btn btn-primary">
                Apply
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

export function EditToolbar({ exec, getDoc }: Props) {
  // Pointer (not mouse) so the preventDefault fires at the true gesture start on
  // touch too, preserving the iframe text selection through the tap.
  const hold = (e: React.PointerEvent) => e.preventDefault();

  // DOM-mutating ops (size span rewrite, vertical align) don't fire `input`, so
  // nudge the editor's autosave by dispatching one.
  const markEdited = () => {
    getDoc()?.body?.dispatchEvent(new Event("input", { bubbles: true }));
  };

  // Apply an exact px size: force <font size=7> output, then rewrite it to a span
  // with the chosen font-size (reliable across browsers vs. the legacy 1–7 scale).
  const applyFontSize = (px: string) => {
    const doc = getDoc();
    if (!doc) return;
    doc.execCommand("styleWithCSS", false, "false");
    doc.execCommand("fontSize", false, "7");
    doc.execCommand("styleWithCSS", false, "true");
    doc.querySelectorAll('font[size="7"]').forEach((el) => {
      const span = doc.createElement("span");
      span.style.fontSize = `${px}px`;
      while (el.firstChild) span.appendChild(el.firstChild);
      el.replaceWith(span);
    });
    markEdited();
  };

  // Vertical alignment targets the selection's nearest slide/section and lays its
  // content out as a column (best-effort; most meaningful on full-height slides).
  const applyVerticalAlign = (pos: string) => {
    const doc = getDoc();
    if (!doc) return;
    const anchor = doc.getSelection?.()?.anchorNode ?? null;
    const start =
      anchor && anchor.nodeType === 1
        ? (anchor as Element)
        : anchor?.parentElement ?? null;
    const section =
      start?.closest("section.section, section, [data-slide]") ??
      start?.closest("section, article, div") ??
      null;
    if (!section) return;
    const s = section as HTMLElement;
    s.style.display = "flex";
    s.style.flexDirection = "column";
    s.style.justifyContent = pos;
    markEdited();
  };

  return (
    <div className="edit-toolbar" role="toolbar" aria-label="Formatting">
      {BLOCKS.map((b) => (
        <button
          key={b.tag}
          type="button"
          className="btn btn-ghost edit-tool edit-tool--text"
          data-tip={b.tip}
          onPointerDown={hold}
          onClick={() => exec("formatBlock", b.tag)}
        >
          {b.label}
        </button>
      ))}
      <span className="edit-tool-sep" aria-hidden="true" />

      <ToolDropdown label="Font" tip="Font — brand typefaces only (Nunito, Montserrat, Rubik)" width={200}>
        {(close) => (
          <div className="tool-list">
            {FONTS.map((f) => (
              <button
                key={f.label}
                type="button"
                className="tool-list-item"
                style={{ fontFamily: f.css }}
                onPointerDown={hold}
                onClick={() => {
                  exec("fontName", f.css);
                  close();
                }}
              >
                <span>{f.label}</span>
                <span className="caption">{f.note}</span>
              </button>
            ))}
          </div>
        )}
      </ToolDropdown>

      <ToolDropdown label="Size" tip="Font size — set the exact text size in pixels" width={88}>
        {(close) => (
          <div className="tool-list tool-list--sizes">
            {SIZES.map((s) => (
              <button
                key={s}
                type="button"
                className="tool-list-item"
                onPointerDown={hold}
                onClick={() => {
                  applyFontSize(s);
                  close();
                }}
              >
                {s}
              </button>
            ))}
          </div>
        )}
      </ToolDropdown>

      <ToolDropdown
        label={<Icon name="align-center" size={16} />}
        tip="Alignment — horizontal text alignment and vertical placement on the slide"
        width={172}
      >
        {(close) => (
          <div className="align-pop">
            <div className="align-row" role="group" aria-label="Horizontal alignment">
              {ALIGN_H.map((a) => (
                <button
                  key={a.command}
                  type="button"
                  className="btn btn-ghost icon-btn"
                  data-tip={a.tip}
                  aria-label={a.tip}
                  onPointerDown={hold}
                  onClick={() => {
                    exec(a.command);
                    close();
                  }}
                >
                  <Icon name={a.icon} size={18} />
                </button>
              ))}
            </div>
            <div className="align-row" role="group" aria-label="Vertical alignment">
              {ALIGN_V.map((a) => (
                <button
                  key={a.pos}
                  type="button"
                  className="btn btn-ghost icon-btn"
                  data-tip={a.tip}
                  aria-label={a.tip}
                  onPointerDown={hold}
                  onClick={() => {
                    applyVerticalAlign(a.pos);
                    close();
                  }}
                >
                  <Icon name={a.icon} size={18} />
                </button>
              ))}
            </div>
          </div>
        )}
      </ToolDropdown>

      <ToolDropdown
        label={<Icon name="edit" size={16} />}
        tip="Text color — apply a brand-palette color to the selected text"
        width={148}
      >
        {(close) => (
          <div className="swatch-grid">
            {TEXT_COLORS.map((c) => (
              <button
                key={c.name}
                type="button"
                className="swatch"
                style={{ background: c.hex }}
                data-tip={`Text · ${c.name}`}
                aria-label={`Text ${c.name}`}
                onPointerDown={hold}
                onClick={() => {
                  exec("foreColor", c.hex);
                  close();
                }}
              />
            ))}
          </div>
        )}
      </ToolDropdown>

      <ToolDropdown label="Highlight" tip="Highlight — apply a brand-palette background behind the text" width={148}>
        {(close) => (
          <div className="swatch-grid">
            {HIGHLIGHTS.map((c) => (
              <button
                key={c.name}
                type="button"
                className={`swatch${c.hex === "transparent" ? " swatch--none" : ""}`}
                style={c.hex === "transparent" ? undefined : { background: c.hex }}
                data-tip={c.name === "None" ? "Remove highlight" : `Highlight · ${c.name}`}
                aria-label={`Highlight ${c.name}`}
                onPointerDown={hold}
                onClick={() => {
                  exec("hiliteColor", c.hex === "transparent" ? "transparent" : c.hex);
                  close();
                }}
              />
            ))}
          </div>
        )}
      </ToolDropdown>

      <span className="edit-tool-sep" aria-hidden="true" />
      {FORMAT.map((a) => (
        <button
          key={a.command}
          type="button"
          className="btn btn-ghost edit-tool icon-btn"
          data-tip={a.tip}
          aria-label={a.title}
          onPointerDown={hold}
          onClick={() => exec(a.command)}
        >
          <Icon name={a.icon} size={18} />
        </button>
      ))}
      <LinkTool exec={exec} getDoc={getDoc} />
      <span className="edit-tool-sep" aria-hidden="true" />
      <button
        type="button"
        className="btn btn-ghost edit-tool icon-btn"
        data-tip="Undo (⌘/Ctrl+Z) the last change"
        aria-label="Undo"
        onPointerDown={hold}
        onClick={() => exec("undo")}
      >
        <Icon name="undo" size={18} />
      </button>
      <button
        type="button"
        className="btn btn-ghost edit-tool icon-btn"
        data-tip="Redo the last undone change"
        aria-label="Redo"
        onPointerDown={hold}
        onClick={() => exec("redo")}
      >
        <Icon name="redo" size={18} />
      </button>
    </div>
  );
}
